<template>
  <div class="admin-tab-content">
    <div class="tab-header">
      <h2>Vuelve Promo</h2>
      <p class="description">Seguimiento de usuarios que han aceptado la promoción de reactivación</p>
    </div>

    <div class="summary-grid">
      <div class="summary-card">
        <span class="summary-label">Total</span>
        <strong>{{ entries.length }}</strong>
      </div>
      <div class="summary-card">
        <span class="summary-label">Activas</span>
        <strong>{{ countByStatus('accepted') }}</strong>
      </div>
      <div class="summary-card">
        <span class="summary-label">Vencidas</span>
        <strong>{{ dueEntries.length }}</strong>
      </div>
      <div class="summary-card">
        <span class="summary-label">Convertidas</span>
        <strong>{{ countByStatus('converted') }}</strong>
      </div>
      <div class="summary-card">
        <span class="summary-label">Fallidas</span>
        <strong>{{ countByStatus('failed') }}</strong>
      </div>
    </div>

    <div class="entries-section">
      <div class="section-header">
        <div>
          <h3>Solicitudes recibidas</h3>
          <p class="section-description">Datos no sensibles recibidos desde el formulario público. El CIF/NIF y el IBAN solo llegan por email a administración.</p>
        </div>
        <button @click="loadAll" class="btn-secondary" :disabled="loading || loadingLeads">
          {{ loading || loadingLeads ? 'Actualizando...' : 'Actualizar todo' }}
        </button>
      </div>

      <div class="manual-activation">
        <label>
          Activar promo por UID
          <input v-model.trim="manualUid" class="admin-input" type="text" inputmode="numeric" placeholder="UID avanzado_off">
        </label>
        <button @click="activatePromo()" class="btn-primary" :disabled="activating || !manualUid">
          {{ activating ? 'Activando...' : 'Activar' }}
        </button>
      </div>

      <div v-if="leads.length > 0" class="table-wrapper">
        <table class="entries-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Email</th>
              <th>Teléfono</th>
              <th>Nombre fiscal</th>
              <th>Dirección fiscal</th>
              <th>Estado</th>
              <th>Solicitud</th>
              <th>UID</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="lead in leads" :key="`lead-${lead.id}`">
              <td>{{ lead.id }}</td>
              <td>{{ lead.email }}</td>
              <td>{{ lead.telephone }}</td>
              <td>{{ lead.tax_name }}</td>
              <td>{{ lead.tax_address }}</td>
              <td>
                <span :class="['status-badge', `status-${lead.status}`]">
                  {{ getLeadStatusLabel(lead.status) }}
                </span>
              </td>
              <td>{{ lead.submitted_at_formatted || '-' }}</td>
              <td>
                <input
                  v-model.trim="leadUids[lead.id]"
                  class="admin-input admin-input--compact"
                  type="text"
                  inputmode="numeric"
                  :disabled="lead.status === 'activated' || activating"
                >
              </td>
              <td class="actions-cell">
                <button
                  v-if="lead.status !== 'activated'"
                  @click="activatePromo(lead)"
                  class="btn-primary btn-primary--small"
                  :disabled="activating || !leadUids[lead.id]"
                >
                  Activar
                </button>
                <span v-else class="no-action">Procesada</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="no-data">
        No hay solicitudes recibidas
      </div>
    </div>

    <div class="entries-section">
      <div class="section-header">
        <div>
          <h3>Usuarios de la promoción</h3>
          <p class="section-description">El vencimiento es la fecha a partir de la que el cron intentará pasarlos a Gold.</p>
        </div>
        <div class="header-actions">
          <select v-model="statusFilter" class="admin-select">
            <option value="all">Todos los estados</option>
            <option value="accepted">Activas</option>
            <option value="converted">Convertidas</option>
            <option value="cancelled">Canceladas</option>
            <option value="failed">Fallidas</option>
          </select>
          <button @click="loadEntries" class="btn-secondary" :disabled="loading">
            {{ loading ? 'Actualizando...' : 'Actualizar' }}
          </button>
        </div>
      </div>

      <div v-if="filteredEntries.length > 0" class="table-wrapper">
        <table class="entries-table">
          <thead>
            <tr>
              <th>UID</th>
              <th>Usuario</th>
              <th>Email</th>
              <th>Estado</th>
              <th>Aceptación</th>
              <th>Vencimiento</th>
              <th>Conversión</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="entry in filteredEntries"
              :key="entry.id"
              :class="{ 'due-date': isDue(entry) }"
            >
              <td>{{ entry.uid }}</td>
              <td>{{ entry.username || '-' }}</td>
              <td>{{ entry.email }}</td>
              <td>
                <span :class="['status-badge', `status-${entry.status}`]">
                  {{ getStatusLabel(entry.status) }}
                </span>
              </td>
              <td>{{ getFormattedDate(entry, 'accepted_at') }}</td>
              <td>
                <span :class="{ 'date-warning': isDue(entry) }">
                  {{ getFormattedDate(entry, 'expires_at') }}
                </span>
              </td>
              <td>{{ getFormattedDate(entry, 'converted_at') }}</td>
              <td class="actions-cell">
                <button
                  v-if="entry.status === 'accepted'"
                  @click="cancelPromo(entry)"
                  class="btn-danger"
                  :disabled="saving"
                >
                  Cancelar
                </button>
                <span v-else class="no-action">Sin acciones</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="no-data">
        No hay usuarios para el filtro seleccionado
      </div>

      <div class="table-explanation">
        <p>Las filas vencidas y aún activas aparecen resaltadas. Si el cron falla, el usuario puede localizarse aquí por UID, email y fecha de vencimiento.</p>
        <p>Cancelar marca la promo como cancelada y devuelve al usuario a `avanzado_off` si todavía sigue como Pre Eureker.</p>
      </div>
    </div>

    <div v-if="message" :class="['message-box', messageType]">
      {{ message }}
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'VuelvePromoTab',
  data() {
    return {
      entries: [],
      leads: [],
      leadUids: {},
      manualUid: '',
      loading: false,
      loadingLeads: false,
      saving: false,
      activating: false,
      statusFilter: 'all',
      message: '',
      messageType: ''
    };
  },
  computed: {
    filteredEntries() {
      if (this.statusFilter === 'all') {
        return this.entries;
      }

      return this.entries.filter(entry => entry.status === this.statusFilter);
    },
    dueEntries() {
      return this.entries.filter(entry => this.isDue(entry));
    }
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    async loadAll() {
      await Promise.all([
        this.loadEntries(),
        this.loadLeads()
      ]);
    },
    async loadEntries() {
      this.loading = true;
      this.message = '';

      try {
        const response = await axios.get(`${process.env.VUE_APP_API}/adminmenu/vuelve-promo/get-all`);

        if (response.data.success) {
          this.entries = response.data.entries || [];
        } else {
          this.showMessage(response.data.message || 'Error al cargar la promoción', 'error');
        }
      } catch (error) {
        this.showMessage('Error al cargar los usuarios de la promoción', 'error');
      } finally {
        this.loading = false;
      }
    },
    async loadLeads() {
      this.loadingLeads = true;

      try {
        const response = await axios.get(`${process.env.VUE_APP_API}/adminmenu/vuelve-promo/get-leads`);

        if (response.data.success) {
          this.leads = response.data.leads || [];
          this.leads.forEach(lead => {
            this.$set(this.leadUids, lead.id, lead.uid && parseInt(lead.uid, 10) ? String(lead.uid) : '');
          });
        } else {
          this.showMessage(response.data.message || 'Error al cargar las solicitudes', 'error');
        }
      } catch (error) {
        this.showMessage('Error al cargar las solicitudes de la promoción', 'error');
      } finally {
        this.loadingLeads = false;
      }
    },
    async activatePromo(lead) {
      const uid = lead ? this.leadUids[lead.id] : this.manualUid;
      if (!uid) {
        this.showMessage('Indica un UID para activar la promoción', 'error');
        return;
      }

      const confirmText = lead
        ? `¿Activar la promoción para el UID ${uid} asociado a ${lead.email}?`
        : `¿Activar la promoción para el UID ${uid}?`;

      if (!confirm(confirmText)) {
        return;
      }

      this.activating = true;
      this.message = '';

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API}/adminmenu/vuelve-promo/activate`,
          {
            uid,
            lead_id: lead ? lead.id : 0
          }
        );

        if (response.data.success) {
          this.showMessage('Promoción activada correctamente', 'success');
          this.manualUid = '';
          await this.loadAll();
        } else {
          this.showMessage(response.data.message || 'No se ha podido activar la promoción', 'error');
        }
      } catch (error) {
        this.showMessage('Error al activar la promoción', 'error');
      } finally {
        this.activating = false;
      }
    },
    async cancelPromo(entry) {
      if (!confirm(`¿Cancelar la promoción de ${entry.email}?`)) {
        return;
      }

      this.saving = true;
      this.message = '';

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API}/adminmenu/vuelve-promo/cancel`,
          {
            uid: entry.uid,
            campaign_id: entry.campaign_id
          }
        );

        if (response.data.success) {
          this.showMessage('Promoción cancelada correctamente', 'success');
          await this.loadEntries();
        } else {
          this.showMessage(response.data.message || 'No se ha podido cancelar la promoción', 'error');
        }
      } catch (error) {
        this.showMessage('Error al cancelar la promoción', 'error');
      } finally {
        this.saving = false;
      }
    },
    countByStatus(status) {
      return this.entries.filter(entry => entry.status === status).length;
    },
    getStatusLabel(status) {
      const labels = {
        accepted: 'Activa',
        cancelled: 'Cancelada',
        converted: 'Convertida',
        failed: 'Fallida'
      };

      return labels[status] || status;
    },
    getLeadStatusLabel(status) {
      const labels = {
        pending: 'Pendiente',
        activated: 'Activada'
      };

      return labels[status] || status;
    },
    getFormattedDate(entry, field) {
      const formattedField = `${field}_formatted`;
      if (entry[formattedField]) {
        return entry[formattedField];
      }

      if (!entry[field]) {
        return '-';
      }

      const timestamp = parseInt(entry[field], 10);
      if (!timestamp) {
        return '-';
      }

      return new Date(timestamp * 1000).toLocaleString('es-ES');
    },
    isDue(entry) {
      const expiresAt = parseInt(entry.expires_at, 10);
      return entry.status === 'accepted' && expiresAt > 0 && expiresAt <= Math.floor(Date.now() / 1000);
    },
    showMessage(msg, type) {
      this.message = msg;
      this.messageType = type;

      if (type === 'success') {
        setTimeout(() => {
          this.message = '';
        }, 3000);
      }
    }
  }
};
</script>

<style scoped>
.admin-tab-content {
  padding: 20px;
}

.tab-header h2 {
  color: #3498db;
  font-size: 24px;
  margin-bottom: 10px;
}

.description {
  color: #bbb;
  margin-bottom: 30px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.summary-card,
.entries-section {
  background: #2c2c2c;
  border-radius: 6px;
}

.summary-card {
  border-left: 3px solid #3498db;
  padding: 16px;
}

.summary-label {
  color: #aaa;
  display: block;
  font-size: 13px;
  margin-bottom: 8px;
}

.summary-card strong {
  color: #fff;
  font-size: 26px;
  font-weight: 500;
}

.entries-section {
  padding: 20px;
  margin-bottom: 20px;
}

.entries-section h3 {
  color: #3498db;
  margin: 0 0 6px;
  font-size: 18px;
}

.section-description {
  color: #aaa;
  font-size: 13px;
  margin: 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 15px;
}

.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.manual-activation {
  align-items: flex-end;
  background: #333;
  border-left: 3px solid #3498db;
  border-radius: 4px;
  display: flex;
  gap: 12px;
  margin-bottom: 18px;
  padding: 15px;
}

.manual-activation label {
  color: #e6e6e6;
  display: flex;
  flex: 1;
  flex-direction: column;
  font-size: 14px;
  gap: 8px;
}

.admin-select {
  background-color: #333;
  border: 1px solid #444;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  height: 38px;
  padding: 0 10px;
}

.admin-input {
  background-color: #333;
  border: 1px solid #444;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  height: 38px;
  padding: 0 10px;
}

.admin-input--compact {
  max-width: 110px;
}

.btn-primary,
.btn-secondary,
.btn-danger {
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  font-size: 14px;
  padding: 10px 16px;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-primary {
  background: #3498db;
}

.btn-primary:hover:not(:disabled) {
  background: #2980b9;
}

.btn-primary--small {
  font-size: 12px;
  padding: 6px 12px;
}

.btn-secondary {
  background: #95a5a6;
}

.btn-secondary:hover:not(:disabled) {
  background: #7f8c8d;
}

.btn-danger {
  background: #e74c3c;
  font-size: 12px;
  padding: 6px 12px;
}

.btn-danger:hover:not(:disabled) {
  background: #c0392b;
}

.btn-primary:disabled,
.btn-secondary:disabled,
.btn-danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.table-wrapper {
  overflow-x: auto;
}

.entries-table {
  width: 100%;
  border-collapse: collapse;
  background: #333;
  border-radius: 4px;
  overflow: hidden;
}

.entries-table th {
  background: #444;
  color: #e6e6e6;
  font-weight: 500;
  padding: 12px 10px;
  text-align: left;
  border-bottom: 1px solid #555;
}

.entries-table td {
  padding: 10px;
  border-bottom: 1px solid #444;
  color: #e6e6e6;
  vertical-align: middle;
}

.entries-table tr:hover {
  background: #3a3a3a;
}

.entries-table tr.due-date {
  background: rgba(231, 76, 60, 0.1);
}

.date-warning {
  color: #e74c3c;
  font-weight: 500;
}

.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-accepted {
  background: #3498db;
  color: #fff;
}

.status-converted {
  background: #2ecc71;
  color: #fff;
}

.status-cancelled {
  background: #95a5a6;
  color: #fff;
}

.status-failed {
  background: #e74c3c;
  color: #fff;
}

.status-pending {
  background: #f39c12;
  color: #fff;
}

.status-activated {
  background: #2ecc71;
  color: #fff;
}

.actions-cell {
  min-width: 110px;
}

.no-action {
  color: #888;
  font-size: 13px;
  font-style: italic;
}

.no-data {
  text-align: center;
  padding: 30px;
  color: #bbb;
  font-style: italic;
  background: #333;
  border-radius: 4px;
}

.message-box {
  background-color: #333;
  border-radius: 4px;
  margin-top: 15px;
  padding: 10px;
  border-left: 3px solid #3498db;
}

.message-box.success {
  border-left-color: #2ecc71;
  color: #2ecc71;
}

.message-box.error {
  border-left-color: #e74c3c;
  color: #e74c3c;
}

.table-explanation {
  margin-top: 20px;
  padding: 15px;
  background: #2c2c2c;
  border-radius: 4px;
  border-left: 3px solid #3498db;
}

.table-explanation p {
  margin: 8px 0;
  color: #bbb;
  font-size: 14px;
  line-height: 1.5;
}

@media (max-width: 768px) {
  .section-header,
  .header-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .admin-select,
  .admin-input,
  .manual-activation,
  .btn-secondary {
    width: 100%;
  }

  .manual-activation {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
